// Service layer for the Account Enrollment feature. One function per backend
// endpoint (plans/account-enrollment-backend-spec.md section 3) plus the option
// lists the wizard needs. Every call goes through APIService so the CSRF and
// refresh interceptors apply; components never build URLs themselves.

import APIService from "../../Service/APIService";

let apiInstance = null;
const api = () => {
  if (!apiInstance) apiInstance = new APIService();
  return apiInstance;
};

const query = (params) => {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    search.set(key, String(value));
  });
  const text = search.toString();
  return text ? `?${text}` : "";
};

const unwrapList = (response) =>
  Array.isArray(response?.data?.data) ? response.data.data : [];

// Error codes the backend returns for this feature. Kept here so components
// compare against constants instead of string literals.
export const ENROLLMENT_ERROR_CODES = {
  ACCOUNT_ALREADY_EXISTS: "ACCOUNT_ALREADY_EXISTS",
  BAA_UPLOAD_FAILED: "BAA_UPLOAD_FAILED",
  INVALID_BAA_FILE: "INVALID_BAA_FILE",
  INVALID_ACCOUNT_CONTACT: "INVALID_ACCOUNT_CONTACT",
  ACCOUNT_CONTACT_EMAIL_IN_USE: "ACCOUNT_CONTACT_EMAIL_IN_USE",
  ALREADY_REVIEWED: "ACCOUNT_ENROLLMENT_ALREADY_REVIEWED",
  PRODUCT_GROUP_CHANGED: "PRODUCT_GROUP_CHANGED",
  PRODUCT_GROUP_NOT_ELIGIBLE: "PRODUCT_GROUP_NOT_ELIGIBLE",
  PRODUCT_NOT_IN_GROUP: "PRODUCT_NOT_IN_GROUP",
  DISTRIBUTOR_INACTIVE: "DISTRIBUTOR_INACTIVE",
  DISTRIBUTOR_MEMBERSHIP_REQUIRED: "DISTRIBUTOR_MEMBERSHIP_REQUIRED",
  CSRF_TOKEN_INVALID: "CSRF_TOKEN_INVALID",
};

export const getEnrollmentErrorCode = (error) =>
  error?.response?.data?.code || null;

export const getEnrollmentErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  const status = error?.response?.status;
  if (data?.message) return data.message;
  if (status === 403) return "You do not have permission to perform this action.";
  if (status === 401) return "Your session has expired. Please sign in again.";
  if (!error?.response) return "Unable to reach the server. Check your connection and try again.";
  return fallback;
};

// ---------------------------------------------------------------------------
// Option lists (existing endpoints)
// ---------------------------------------------------------------------------

export const getDistributorOptions = async () => {
  const response = await api().getDataFromBackend("getdistributors");
  return unwrapList(response).map((row) => ({
    value: Number(row.ID),
    label: row.DistributorName,
  }));
};

export const getRegionOptions = async () => {
  const response = await api().getDataFromBackend("getallregions");
  return unwrapList(response).map((row) => ({
    value: Number(row.ID),
    label: row.Region,
  }));
};

// Whole StatesMaster rows: the abbreviation drives the State option and the
// RegionID / MAC / DME_MAC columns drive the derived fields.
export const getStateRows = async () => {
  const response = await api().getDataFromBackend("getstatesmaster");
  return unwrapList(response);
};

// Account.State stores the abbreviation (see AccountDetailsTab), so the option
// value is the abbreviation, not the StatesMaster ID.
export const stateRowsToOptions = (rows = []) =>
  (rows || [])
    .map((row) => String(row.Abbreviation || "").trim())
    .filter(Boolean)
    .map((abbreviation) => ({ value: abbreviation, label: abbreviation }));

export const getStateOptions = async () => stateRowsToOptions(await getStateRows());

// ---------------------------------------------------------------------------
// Enrollment endpoints
// ---------------------------------------------------------------------------

export const getDistributorActiveManufacturers = async (distributorId) => {
  const response = await api().getDataFromBackend(
    `getdistributoractivemanufacturers${query({ DistributorID: distributorId })}`,
  );
  return unwrapList(response);
};

export const getDpaProductGroups = async (distributorId) => {
  const response = await api().getDataFromBackend(
    `getdpaproductgroups${query({ DistributorID: distributorId })}`,
  );
  return unwrapList(response);
};

export const checkAccountDuplicates = async ({ AccountName, NPI, TIN }) => {
  const response = await api().getDataFromBackend(
    `checkaccountduplicates${query({ AccountName, NPI, TIN })}`,
  );
  return unwrapList(response);
};

export const BAA_FILE_MAX_BYTES = 4 * 1024 * 1024;

// Client-side mirror of the backend's BAA rules (optional, PDF, ≤ 4 MB).
export const validateBaaFile = (file) => {
  if (!file) return "";
  const name = String(file.name || "").toLowerCase();
  if (!name.endsWith(".pdf") && file.type !== "application/pdf") {
    return "The BAA must be a PDF file.";
  }
  if (Number(file.size) > BAA_FILE_MAX_BYTES) {
    return "BAA file size should be less than 4 MB.";
  }
  return "";
};

export const buildEnrollmentRequestBody = ({
  Account,
  AccountContacts,
  OfferType,
  DiscountMode,
  DefaultDiscountPercentage,
  ProductGroups,
}) => ({
  Account,
  AccountContacts: AccountContacts || [],
  OfferType,
  DiscountMode: DiscountMode ?? null,
  DefaultDiscountPercentage: DefaultDiscountPercentage ?? null,
  // The backend snapshots names/prices itself; only IDs and the values
  // for the active mode are sent (spec section 3.4).
  ProductGroups: (ProductGroups || []).map((group) => ({
    ProductGroupID: group.ProductGroupID,
    DiscountPercentage: group.DiscountPercentage ?? null,
    PricePerSquare: group.PricePerSquare ?? null,
    Products: (group.Products || []).map((product) => ({
      ProductID: product.ProductID,
      DiscountPercentage: product.DiscountPercentage ?? null,
    })),
  })),
});

export const createAccountEnrollmentRequest = async ({ file = null, ...payload }) => {
  const body = buildEnrollmentRequestBody(payload);
  let response;
  if (file) {
    // With a BAA the request is multipart: JSON in `data` next to `file`,
    // the same shape AddAccount uses.
    const formData = new FormData();
    formData.append("file", file);
    formData.append("data", JSON.stringify(body));
    response = await api().addFileDataToBackend(
      formData,
      "createaccountenrollmentrequest",
      { headers: { "Content-Type": "multipart/form-data" } },
    );
  } else {
    response = await api().addDataToBackend(body, "createaccountenrollmentrequest");
  }
  return response?.data?.data || null;
};

// Reviewers download the attached BAA through the existing SAS-token flow.
export const getEnrollmentBaaDownloadUrl = async (blobName) => {
  if (!blobName) return null;
  const sasUrl = await api().getSasToken(blobName);
  if (!sasUrl) return null;
  const exists = await api().checkBlobExists(sasUrl);
  return exists ? sasUrl : null;
};

export const getAccountEnrollmentRequests = async ({
  status = "Pending",
  page = 1,
  pageSize = 20,
  search = "",
  sortField = "createdAt",
  sortOrder = "desc",
} = {}) => {
  const response = await api().getDataFromBackend(
    `getaccountenrollmentrequests${query({ status, page, pageSize, search, sortField, sortOrder })}`,
  );
  return {
    data: unwrapList(response),
    totalCount: Number(response?.data?.totalCount) || 0,
    totalPages: Number(response?.data?.totalPages) || 0,
  };
};

export const getAccountEnrollmentRequestById = async (id) => {
  const response = await api().getDataFromBackend(
    `getaccountenrollmentrequestbyid${query({ ID: id })}`,
  );
  return response?.data?.data || null;
};

export const reviewAccountEnrollmentRequest = async ({
  id,
  action,
  rejectionReason,
}) => {
  const response = await api().addDataToBackend(
    {
      RequestID: id,
      ...(action === "reject"
        ? { RejectionReason: String(rejectionReason ?? "").trim() }
        : {}),
    },
    `updateaccountenrollmentrequest${query({ type: action })}`,
  );
  return response?.data?.data || null;
};
